import { useEffect } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import type { TenantProjectionStore } from '@ovasabi/frontend-kit'
import { MobileShell } from '../../components/chow'
import { prototypeStoreRegistry } from '../../generated/prototypeRuntime'
import { PageTransition } from '../../styles/PageTransition'
import { createPrototypeRuntimeContext } from '../../stores/prototype'
import { publishProjectionStatus } from '../../lib/projectionHealth'
import {
  resetActivePrototypeRuntime,
  setActivePrototypeRuntime,
} from '../../stores/activeRuntime'
import { demoOffers } from './demoData'
import { EarningsPage } from './EarningsPage'
import { HomePage } from './HomePage'
import { InboxPage } from './InboxPage'
import { JobsPage } from './JobsPage'

type Store = TenantProjectionStore<Record<string, unknown>>

const getStore = (key: string): Store | undefined =>
  prototypeStoreRegistry.get(key) as unknown as Store | undefined

const isJWT = (token: string | null): token is string =>
  Boolean(token && token.split('.').length === 3)

// The organization claim scopes the live projection runtime; without it the
// read model filters out every server record (see ChooseChowApp.orgFromToken).
const orgFromToken = (token: string): string => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1])) as { organization_id?: string }
    return String(payload.organization_id ?? '').trim()
  } catch {
    return ''
  }
}

/** Seconds each seeded offer stays live, in the order the fixtures are listed. */
const OFFER_LIFETIME_SECONDS = [5 * 60, 4 * 60, 3 * 60] as const

/** The third fixture is a scheduled run, to show the badge and the Jobs panel. */
const SCHEDULED_FIXTURE_INDEX = 2

/**
 * Fixtures for the offline lane, written in the shape the server projects.
 *
 * The addresses use `pickup_address` / `dropoff_address` — the names in the
 * deliveries projection allowlist — rather than the older `pickup` / `dropoff`.
 * Seeding the legacy keys is how the demo drifted from production: the cards
 * fell back to placeholder text because the real field names were not there.
 */
const seedRiderPreview = () => {
  const now = Date.now()
  getStore('rider.rider_profiles')?.replace([
    {
      id: 'rider-prototype',
      organization_id: 'org-prototype',
      full_name: 'Tobi Adebayo',
      vehicle_type: 'motorbike',
      status: 'active',
      rating_avg: 4.92,
      rating_count: 312,
    },
  ])
  getStore('offer.offers')?.replace(
    demoOffers.map((offer, index) => ({
      id: `offer-${index + 1}`,
      organization_id: 'org-prototype',
      delivery_id: `delivery-${index + 1}`,
      restaurant_name: offer.merchant,
      pickup_address: offer.pickup,
      dropoff_address: offer.dropoff,
      pickup_distance_km: offer.distanceKm,
      eta_minutes: offer.etaMins,
      pay_total_minor: offer.payNgn * 100,
      currency: 'NGN',
      peak_pay: Boolean(offer.surge),
      state: 'offered',
      source_app: 'choosechow',
      expires_at: new Date(
        now + (OFFER_LIFETIME_SECONDS[index] ?? 3 * 60) * 1000,
      ).toISOString(),
      scheduled_at:
        index === SCHEDULED_FIXTURE_INDEX
          ? new Date(now + 3 * 60 * 60 * 1000).toISOString()
          : undefined,
    })),
  )
  getStore('delivery.deliveries')?.replace([])
  getStore('logistics.rider_locations')?.replace([
    {
      id: 'location-rider-prototype',
      rider_id: 'rider-prototype',
      dash_session_id: 'dash-preview',
      latitude: 6.5244,
      longitude: 3.3792,
      status: 'online',
      created_at: new Date(now).toISOString(),
    },
  ])
  getStore('support.support_messages')?.replace([
    {
      id: 'support-welcome',
      organization_id: 'org-prototype',
      thread_id: 'rider-rider-prototype',
      sender_id: 'dispatch',
      sender_type: 'Dispatch',
      body: 'You are online in the Yaba–Akoka zone. We will keep trip and safety updates in this chat.',
      created_at: new Date(now - 4 * 60 * 1000).toISOString(),
    },
  ])
  getStore('wallet.wallet_transactions')?.replace([
    {
      id: 'earning-1',
      transaction_type: 'Delivery CHW-4821',
      amount_minor: 185000,
      currency: 'NGN',
      status: 'settled',
      created_at: new Date(now - 50 * 60 * 1000).toISOString(),
    },
    {
      id: 'earning-2',
      transaction_type: 'Peak delivery bonus',
      amount_minor: 45000,
      currency: 'NGN',
      status: 'settled',
      created_at: new Date(now - 2 * 60 * 60 * 1000).toISOString(),
    },
  ])
}

const RiderRuntimeBridge = ({ children }: { children: React.ReactNode }) => {
  useEffect(() => {
    const token = localStorage.getItem('auth_token')
    if (!isJWT(token)) {
      resetActivePrototypeRuntime()
      seedRiderPreview()
      return undefined
    }
    const org = orgFromToken(token)
    const runtime = createPrototypeRuntimeContext({
      tenantId: org || undefined,
      dummyRecordCount: 0,
      live: true,
      projectionHeaders: () => ({ Authorization: `Bearer ${token}` }),
      projectionQuery: () => ({ access_token: token }),
      // Report the delta stream's health instead of dropping it: without this
      // a socket that gives up leaves every screen rendering its last snapshot
      // with nothing to say so. See lib/projectionHealth.
      onProjectionStatus: publishProjectionStatus,
    })
    setActivePrototypeRuntime(runtime)
    return () => runtime.liveBindings.forEach((binding) => binding.disconnect())
  }, [])

  return <>{children}</>
}

/**
 * The rider product.
 *
 * Four routes, one per decision a rider makes: am I on shift (Home), what am I
 * doing now (Jobs), what have I earned (Earnings), and do I need a human
 * (Inbox). Safety and help used to be separate screens; they are sections of the
 * inbox and an SOS control in the session panel instead. See
 * docs/edition_separation.md for the surface this replaced.
 */
export const ChowDashApp = () => {
  const location = useLocation()
  return (
    <RiderRuntimeBridge>
      <MobileShell>
        {/* See ChooseChowApp: the routed view is not gated behind an exit
            animation, because a hidden document never finishes one. */}
        <PageTransition key={location.pathname}>
          <Routes location={location}>
            <Route path="/" element={<HomePage />} />
            <Route path="/jobs" element={<JobsPage />} />
            <Route path="/earnings" element={<EarningsPage />} />
            <Route path="/inbox" element={<InboxPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </PageTransition>
      </MobileShell>
    </RiderRuntimeBridge>
  )
}
